import React, { lazy } from 'react';
import { SectionHeader, CornerBrackets, Button, FadeIn } from '../ui';
type Home = {
  name: string;
  desc: string;
  award: string | null;
  span: string;
  aspect: string;
  image: string;
  imgW: number;
  imgH: number;
  // When the supplied photo already has an award badge baked in,
  // we skip the code-rendered overlay badge to avoid double-badging.
  badgeBaked?: boolean;
  // swap = the real Atrium photo for this home
  swapNote: string;
};
export function CompletedHomes() {
  const homes: Home[] = [{
    name: 'The Ashwood',
    desc: 'A refined modern street presence in white render and timber.',
    award: null,
    span: 'col-span-1 md:col-span-2 row-span-2',
    aspect: 'aspect-[4/3] md:aspect-[16/9]',
    image: 'https://cdn.magicpatterns.com/uploads/eD9VCdZxGmUnt7Moow6jEX/The_AShwood.webp',
    imgW: 800,
    imgH: 533,
    swapNote: 'real The Ashwood exterior'
  }, {
    name: 'The Dianella Residence',
    desc: 'Elegant family living with a calm, considered layout.',
    award: null,
    span: 'col-span-1',
    aspect: 'aspect-square',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    imgW: 800,
    imgH: 800,
    swapNote: 'real The Dianella Residence exterior'
  }, {
    name: 'Kinross Residence',
    desc: 'Architectural detail considered at every junction.',
    award: '2025 MBA Finalist',
    span: 'col-span-1',
    aspect: 'aspect-square',
    image: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    imgW: 800,
    imgH: 800,
    swapNote: 'real Kinross Residence exterior'
  }, {
    name: 'The Long Island',
    desc: 'Hamptons-inspired proportions and crafted joinery.',
    award: '2022 MBA Best Display Home Winner',
    span: 'col-span-1 md:col-span-2',
    aspect: 'aspect-[21/9]',
    image: 'https://cdn.magicpatterns.com/uploads/bFfqtn5g688mguPEvDUnfq/the-long-island_mba-logo.png',
    imgW: 820,
    imgH: 547,
    badgeBaked: true,
    swapNote: 'real The Long Island exterior (award badge already in photo)'
  }, {
    name: 'Applecross Residence',
    desc: 'Sophisticated riverside living, HIA recognised.',
    award: 'HIA Recognised',
    span: 'col-span-1',
    aspect: 'aspect-[4/5]',
    image: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    imgW: 800,
    imgH: 1000,
    swapNote: 'real Applecross Residence exterior'
  }, {
    name: 'The Aspire',
    desc: 'Layered brick, timber and light at dusk.',
    award: null,
    span: 'col-span-1',
    aspect: 'aspect-[4/5]',
    image: 'https://cdn.magicpatterns.com/uploads/woW478fWX3goVkPdF2QJVC/The_Aspire.png',
    imgW: 800,
    imgH: 1000,
    swapNote: 'real The Aspire exterior'
  }, {
    name: 'The Gables',
    desc: 'Classic character with a contemporary hand.',
    award: null,
    span: 'col-span-1',
    aspect: 'aspect-[4/5]',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    imgW: 800,
    imgH: 1000,
    swapNote: 'real The Gables exterior'
  }, {
    name: 'Ardross Residence',
    desc: 'A considered custom design close to home.',
    award: null,
    span: 'col-span-1 md:col-span-3',
    aspect: 'aspect-[21/9]',
    image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    imgW: 1200,
    imgH: 514,
    swapNote: 'real Ardross Residence exterior'
  }];
  return <section id="completed-homes" className="py-16 md:py-24 bg-atrium-ivory scroll-mt-24" data-id="element-117">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-118">
        <FadeIn data-id="element-119">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8" data-id="element-120">
            <SectionHeader eyebrow="Completed Homes" title="A portfolio built to last" className="mb-0" data-id="element-121" />
            <Button variant="secondary" as="a" href="/completed-homes/" data-id="element-122">
              Explore completed homes
            </Button>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 auto-rows-min" data-id="element-123">
          {homes.map((home, index) => <FadeIn key={index} delay={index % 3 * 100} className={home.span} data-id="element-124">
              <div className="group relative block h-full bg-white shadow-diffuse p-4 transition-editorial duration-600 hover:shadow-diffuse-lg" data-id="element-125">
                <CornerBrackets color="bronze" data-id="element-126" />
                <div className={`relative overflow-hidden mb-6 ${home.aspect}`} data-id="element-127">
                  {/* swap = {home.swapNote} */}
                  <img src={home.image} alt={`${home.name}, a custom home by Atrium Homes`} width={home.imgW} height={home.imgH} loading="lazy" className="object-cover w-full h-full group-hover:scale-105 transition-editorial duration-800" data-id="element-128" />
                  {home.award && !home.badgeBaked && <div className="absolute top-4 right-4 bg-atrium-green text-atrium-ivory text-xs font-bold tracking-wider px-3 py-1.5 border border-atrium-bronze/40" data-id="element-129">
                      {home.award}
                    </div>}
                </div>
                <div className="px-2 pb-2" data-id="element-130">
                  <h3 className="font-serif text-2xl text-atrium-ink mb-2" data-id="element-131">
                    {home.name}
                  </h3>
                  <p className="text-sm text-atrium-ink/70 mb-6" data-id="element-132">{home.desc}</p>
                  <Button variant="tertiary" as="a" href={`#${home.name.toLowerCase().replace(/ /g, '-')}`} data-id="element-133">
                    View the home
                  </Button>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}